import { Post } from '../../schema-copy/feed-schema';
import { StoredImage } from './post-store';

/** Images larger than this are not stored. */
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/**
 * The URL an image is stored and looked up under. Feed icons (…/imgproxy/<feedId>/<kind>-w<width>.webp)
 * come in several widths; one stored width serves them all, so the width is dropped.
 */
export function imageKeyUrl(url: string): string {
  return url.replace(/(\/imgproxy\/[^/]+\/[a-z]+)-w\d+\.webp$/, '$1.webp');
}

const IMG_SRC = /<img\b[^>]*?\bsrc\s*=\s*["']([^"']+)["']/gi;

/**
 * Images of a post to store for offline reading: the attached images (lead image, thumbnail, and
 * the media file when it is an image — never video or audio) and every <img> in the body.
 * Absolute http(s) URLs only (relative ones are resolved against the post link), no duplicates.
 */
export function postImageUrls(post: Post): string[] {
  const candidates = [post.imageUrl, post.thumbnailUrl];
  if (post.mediaUrl && post.mediaType?.startsWith('image/')) candidates.push(post.mediaUrl);
  for (const match of Array.from((post.description ?? '').matchAll(IMG_SRC)))
    candidates.push(decodeEntities(match[1]));

  const urls = new Set<string>();
  for (const candidate of candidates) {
    if (!candidate || candidate.startsWith('data:')) continue;
    try {
      const url = new URL(candidate, post.link || undefined);
      if (url.protocol == 'https:' || url.protocol == 'http:') urls.add(url.href);
    } catch { /* not a URL */ }
  }
  return Array.from(urls);
}

// src attributes in the HTML may contain entities, mostly &amp; in query strings.
function decodeEntities(s: string): string {
  return s.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>');
}

/** The server answered, but not with a storable image (HTTP error, not an image, too large).
 *  Retrying won't help. Other errors (network, CORS) may be temporary. */
export class ImageUnavailableError extends Error {}

/** Download an image as binary data, stored under imageKeyUrl(url). */
export async function downloadImage(url: string, signal: AbortSignal): Promise<StoredImage> {
  // No referrer: some hosts refuse hotlinked images that carry one (as FeedIcon does on retry).
  const response = await fetch(url, { mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer', signal });
  if (!response.ok) throw new ImageUnavailableError(`${response.status} for ${url}`);
  const type = response.headers.get('content-type')?.split(';')[0].trim() ?? '';
  if (!type.startsWith('image/')) throw new ImageUnavailableError(`Not an image (${type}): ${url}`);
  if (Number(response.headers.get('content-length')) > MAX_IMAGE_BYTES) throw new ImageUnavailableError(`Too large: ${url}`);
  const data = await response.arrayBuffer();
  if (data.byteLength > MAX_IMAGE_BYTES) throw new ImageUnavailableError(`Too large: ${url}`);
  return { url: imageKeyUrl(url), type, data };
}
